import { BookOpen, ChevronRight, Lightbulb, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const InstructionsPage = () => {
  const { t } = useTranslation();
  
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {t('instructions.title')}
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          {t('instructions.subtitle')}
        </p>
      </div>

      {/* Step-by-step Instructions */}
      <div className="space-y-6">
        {t('instructions.steps', { returnObjects: true }).map((step, index) => (
          <div key={index} className="card">
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1">
                {index + 1}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600 mb-3">
                  {step.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tips Section */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex items-center space-x-2 mb-4">
          <Lightbulb className="text-blue-600" size={24} />
          <h3 className="text-xl font-semibold text-blue-900">
            {t('instructions.tips.title')}
          </h3>
        </div>
        <ul className="space-y-3 text-blue-800">
          {t('instructions.tips.list', { returnObjects: true }).map((tip, index) => (
            <li key={index} className="flex items-start space-x-2">
              <ChevronRight size={16} className="text-blue-600 mt-1 flex-shrink-0" />
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Rating Guide */}
      <div className="card bg-yellow-50 border-yellow-200">
        <div className="flex items-center space-x-2 mb-4">
          <MessageCircle className="text-yellow-600" size={24} />
          <h3 className="text-xl font-semibold text-yellow-900">
            How to Rate Translations (0-100 Scale)
          </h3>
        </div>
        <div className="grid md:grid-cols-5 gap-4 text-sm">
          <div className="text-center">
            <div className="text-lg mb-2 font-bold text-red-600">0-20</div>
            <div className="font-semibold text-yellow-800">Very Poor</div>
            <p className="text-yellow-700 mt-1">Completely incorrect, nonsensical, or "unk" tokens</p>
          </div>
          <div className="text-center">
            <div className="text-lg mb-2 font-bold text-orange-600">21-40</div>
            <div className="font-semibold text-yellow-800">Poor</div>
            <p className="text-yellow-700 mt-1">Many errors, difficult to understand meaning</p>
          </div>
          <div className="text-center">
            <div className="text-lg mb-2 font-bold text-yellow-600">41-60</div>
            <div className="font-semibold text-yellow-800">Fair</div>
            <p className="text-yellow-700 mt-1">Generally understandable but with significant issues</p>
          </div>
          <div className="text-center">
            <div className="text-lg mb-2 font-bold text-lime-600">61-80</div>
            <div className="font-semibold text-yellow-800">Good</div>
            <p className="text-yellow-700 mt-1">Mostly accurate with minor errors or awkward phrasing</p>
          </div>
          <div className="text-center">
            <div className="text-lg mb-2 font-bold text-green-600">81-100</div>
            <div className="font-semibold text-yellow-800">Excellent</div>
            <p className="text-yellow-700 mt-1">Accurate and natural, sounds like native Sranantongo</p>
          </div>
        </div>
      </div>

      {/* Common Issues */}
      <div className="card bg-red-50 border-red-200">
        <div className="flex items-center space-x-2 mb-4">
          <BookOpen className="text-red-600" size={24} />
          <h3 className="text-xl font-semibold text-red-900">
            {t('instructions.troubleshooting.title')}
          </h3>
        </div>
        <div className="space-y-3 text-red-800">
          {t('instructions.troubleshooting.items', { returnObjects: true }).map((item, index) => (
            <div key={index}>
              <p className="font-semibold">Problem: {item.problem}</p>
              <p className="text-sm text-red-700">{item.solution}</p>
            </div>
          ))}
        </div>
      </div>

      {/* What to Expect */}
      <div className="card bg-gray-50 border-gray-200">
        <div className="flex items-center space-x-2 mb-4">
          <BookOpen className="text-gray-600" size={24} />
          <h3 className="text-xl font-semibold text-gray-900">
            {t('instructions.expectations.title')}
          </h3>
        </div>
        <div className="text-gray-700">
          <p>{t('instructions.expectations.description')}</p>
        </div>
      </div>
    </div>
  );
};

export default InstructionsPage;